import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Layout from "@components/Layout";
import Plot from "react-plotly.js";
import { API_BASE_URL } from "@lib/config";
import { fmtLocalDateTime } from "@lib/dateUtils";
import { useUser } from "../hooks/useUser";
import { useSignalR } from "../hooks/useSignalR";
import { isMarketOpenAt } from "@lib/marketSessionClock";
import { useTheme } from "../theme/ThemeProvider";
import { getChartPalette, heatmapColorscale, type ChartPalette, type HeatmapKind } from "../theme/chartTheme";

/* ----------------------- theme-safe UI helpers ----------------------- */
const ui = {
  pageBg: "var(--bg-primary)",
  cardBg: "var(--card-bg)",
  cardBorder: "var(--card-border)",
  textPrimary: "var(--text-primary)",
  textSecondary: "var(--text-secondary)",
  textMuted: "var(--text-muted)",
  shadow: "var(--shadow-soft)",
  buttonBg: "var(--button-bg)",
  buttonBgHover: "var(--button-bg-hover)",
  buttonText: "var(--button-text)",
  sectionBg: "rgba(148, 163, 184, 0.10)",
};

const themeCardStyle: React.CSSProperties = {
  backgroundColor: ui.cardBg,
  border: `1px solid ${ui.cardBorder}`,
  color: ui.textPrimary,
  boxShadow: ui.shadow,
};

const sectionCardStyle: React.CSSProperties = {
  backgroundColor: ui.sectionBg,
  border: `1px solid ${ui.cardBorder}`,
  color: ui.textPrimary,
};

const buttonStyle: React.CSSProperties = {
  backgroundColor: ui.buttonBg,
  color: ui.buttonText,
  border: `1px solid ${ui.cardBorder}`,
};

const mutedText: React.CSSProperties = {
  color: ui.textMuted,
};

const secondaryText: React.CSSProperties = {
  color: ui.textSecondary,
};

type Tone = "neutral" | "success" | "warning" | "orange" | "danger" | "info";

const toneStyles: Record<Tone, React.CSSProperties> = {
  neutral: {
    backgroundColor: "rgba(100, 116, 139, 0.12)",
    borderColor: "rgba(100, 116, 139, 0.28)",
    color: "var(--text-secondary)",
  },
  success: {
    backgroundColor: "rgba(22, 163, 74, 0.12)",
    borderColor: "rgba(22, 163, 74, 0.32)",
    color: "#16a34a",
  },
  warning: {
    backgroundColor: "rgba(217, 119, 6, 0.12)",
    borderColor: "rgba(217, 119, 6, 0.34)",
    color: "#d97706",
  },
  danger: {
    backgroundColor: "rgba(220, 38, 38, 0.12)",
    borderColor: "rgba(220, 38, 38, 0.34)",
    color: "#dc2626",
  },
  info: {
    backgroundColor: "rgba(37, 99, 235, 0.12)",
    borderColor: "rgba(37, 99, 235, 0.32)",
    color: "#2563eb",
  },
  orange: {
    backgroundColor: "rgba(249, 115, 22, 0.14)",
    borderColor: "rgba(249, 115, 22, 0.36)",
    color: "#f97316",
  },
};

const chipStyle = (tone: Tone = "neutral"): React.CSSProperties => ({
  ...toneStyles[tone],
  borderWidth: 1,
  borderStyle: "solid",
});

type PlotlyBundle = {
  data?: any[];
  layout?: any;
};

// ChartPalette + getChartPalette now live in ../theme/chartTheme (shared, single
// source of truth for chart colors). Heatmap colorscales come from the same module
// via heatmapColorscale() so the fill matches the rest of the page instead of the
// backend's hardcoded named scales.

const numericValue = (value: any): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const inferThresholdColor = (value: any, label: any, palette: ChartPalette): string | null => {
  const n = numericValue(value);
  const text = String(label ?? "").toLowerCase();

  if (text.includes("extreme") || text.includes("95") || (n != null && n >= 94)) return palette.thresholdExtreme;
  if (text.includes("elevated") || text.includes("80") || (n != null && n >= 78)) return palette.thresholdElevated;
  return null;
};

const shapeLineColor = (shape: any, palette: ChartPalette): string => {
  return (
    inferThresholdColor(shape?.y0, shape?.name ?? shape?.label?.text ?? shape?.line?.color, palette) ??
    inferThresholdColor(shape?.y1, shape?.name ?? shape?.label?.text ?? shape?.line?.color, palette) ??
    palette.threshold
  );
};

const annotationColors = (annotation: any, palette: ChartPalette) => {
  const semanticColor = inferThresholdColor(annotation?.y, annotation?.text, palette);
  return {
    text: semanticColor ?? palette.annotationText,
    border: semanticColor ?? palette.border,
    bg: palette.annotationBg,
  };
};

const mergeAxisLayout = (axis: any, palette: ChartPalette, extras: Record<string, any> = {}) => ({
  ...axis,
  automargin: true,
  showgrid: axis?.showgrid ?? true,
  gridcolor: palette.grid,
  gridwidth: 1,
  zerolinecolor: palette.zeroLine,
  zerolinewidth: 1.2,
  linecolor: palette.border,
  tickcolor: palette.border,
  tickfont: { ...(axis?.tickfont ?? {}), color: palette.axisMuted, size: 11 },
  title: axis?.title
    ? typeof axis.title === "string"
      ? { text: axis.title, font: { color: palette.axis, size: 12 } }
      : { ...axis.title, font: { ...(axis.title.font ?? {}), color: palette.axis, size: 12 } }
    : undefined,
  ...extras,
});

const styleCommonLayout = (layout: any, palette: ChartPalette, opts: { height?: number } = {}) => ({
  ...layout,
  autosize: true,
  paper_bgcolor: palette.paperBg,
  plot_bgcolor: palette.plotBg,
  height: opts.height ?? layout?.height,
  margin: { l: 56, r: 24, t: 26, b: 46, ...(layout?.margin ?? {}) },
  font: { ...(layout?.font ?? {}), family: "Inter, Segoe UI, sans-serif", color: palette.text, size: 12 },
  title: undefined,
  colorway: [palette.accentBlue, palette.accentOrange, palette.up, palette.down, palette.maLine, palette.thresholdElevated, palette.thresholdExtreme],
  showlegend: layout?.showlegend ?? true,
  legend: {
    orientation: "h",
    x: 0,
    y: 1.12,
    xanchor: "left",
    yanchor: "bottom",
    bgcolor: "rgba(0,0,0,0)",
    borderwidth: 0,
    font: { color: palette.legendText, size: 11 },
    itemclick: false,
    itemdoubleclick: false,
    ...(layout?.legend ?? {}),
  },
  hoverlabel: {
    bgcolor: palette.hoverBg,
    bordercolor: palette.hoverBorder,
    font: { color: palette.text, size: 11 },
    ...(layout?.hoverlabel ?? {}),
  },
  xaxis: mergeAxisLayout(layout?.xaxis, palette),
  yaxis: mergeAxisLayout(layout?.yaxis, palette),
  shapes: Array.isArray(layout?.shapes)
    ? layout.shapes.map((shape: any) => {
        const color = shapeLineColor(shape, palette);
        return {
          ...shape,
          line: {
            ...(shape?.line ?? {}),
            color,
            width: Math.max(shape?.line?.width ?? 1, palette.theme === "dark" ? 1.35 : 1),
            dash: shape?.line?.dash ?? "dot",
          },
        };
      })
    : layout?.shapes,
  annotations: Array.isArray(layout?.annotations)
    ? layout.annotations.map((annotation: any) => {
        const colors = annotationColors(annotation, palette);
        return {
          ...annotation,
          font: { ...(annotation?.font ?? {}), color: colors.text, size: 11 },
          bgcolor: colors.bg,
          bordercolor: colors.border,
          borderwidth: annotation?.borderwidth ?? 1,
          opacity: 0.98,
        };
      })
    : layout?.annotations,
});

const styleShockTapeBundle = (bundle: PlotlyBundle, palette: ChartPalette): PlotlyBundle => ({
  data: (bundle.data ?? []).map((trace: any) => {
    const name = String(trace?.name ?? "").toLowerCase();
    const isToday = name.includes("today");
    const isUp = name.includes("up");
    const isDown = name.includes("down");

    const baseColor = isToday
      ? palette.accent
      : isUp
      ? palette.up
      : isDown
      ? palette.down
      : palette.accentBlue;

    const baseSize = isToday ? 15 : 10;
    const size = Array.isArray(trace?.marker?.size)
      ? trace.marker.size.map((value: number) => Math.min((value ?? baseSize) * (isToday ? 1.15 : 0.92), isToday ? 18 : 16))
      : baseSize;

    return {
      ...trace,
      cliponaxis: false,
      marker: {
        ...(trace?.marker ?? {}),
        color: baseColor,
        opacity: isToday ? 1 : palette.theme === "dark" ? 0.86 : 0.80,
        size,
        line: {
          ...(trace?.marker?.line ?? {}),
          color: isToday ? (palette.theme === "dark" ? "rgba(248,250,252,0.95)" : "rgba(255,255,255,0.98)") : palette.border,
          width: isToday ? 2.2 : 1.1,
        },
      },
      textfont: { ...(trace?.textfont ?? {}), color: palette.axisMuted, size: isToday ? 11 : 10 },
      hovertemplate: trace?.hovertemplate,
    };
  }),
  layout: (() => {
    const layout = styleCommonLayout(bundle.layout, palette, { height: 420 });
    return {
      ...layout,
      height: 420,
      margin: { ...layout.margin, l: 58, r: 20, t: 8, b: 92 },
      showlegend: true,
      legend: {
        ...(layout.legend ?? {}),
        orientation: "h",
        x: 0.5,
        y: -0.18,
        xanchor: "center",
        yanchor: "top",
        bgcolor: "rgba(0,0,0,0)",
        borderwidth: 0,
        font: { ...((layout.legend ?? {}).font ?? {}), color: palette.legendText, size: 11 },
        itemclick: false,
        itemdoubleclick: false,
      },
      xaxis: mergeAxisLayout(layout?.xaxis, palette, { showgrid: true, tickangle: -30, domain: [0, 1] }),
      yaxis: mergeAxisLayout(layout?.yaxis, palette, { zeroline: true, rangemode: "normal", domain: [0, 1] }),
    };
  })(),
});

const styleAbszBundle = (bundle: PlotlyBundle, palette: ChartPalette): PlotlyBundle => ({
  data: (bundle.data ?? []).map((trace: any) => {
    const name = String(trace?.name ?? "").toLowerCase();
    const isDaily = name.includes("daily");
    const isMa = name.includes("5d ma") && !name.includes("live");
    const isToday = name.includes("today");
    const isLiveMa = name.includes("5d ma") && name.includes("live");

    if (isDaily) {
      return {
        ...trace,
        mode: "lines",
        line: { ...(trace?.line ?? {}), color: palette.dailyLine, width: palette.theme === "dark" ? 1.6 : 1.25 },
        opacity: palette.dailyLineOpacity,
        hoverinfo: trace?.hoverinfo ?? "skip",
      };
    }

    if (isMa) {
      return {
        ...trace,
        mode: "lines",
        line: { ...(trace?.line ?? {}), color: palette.maLine, width: palette.theme === "dark" ? 3.4 : 3 },
        opacity: 1,
      };
    }

    if (isToday || isLiveMa) {
      // Live 5D-MA marker shares its orange line color; differentiate live-vs-daily
      // by SHAPE (diamond vs circle), never by a clashing hue.
      const color = isToday ? palette.todayMarker : palette.maLine;
      const symbol = isToday ? "circle" : "diamond";
      return {
        ...trace,
        mode: "markers+text",
        textposition: "middle right",
        cliponaxis: false,
        marker: {
          ...(trace?.marker ?? {}),
          color,
          symbol,
          size: isToday ? 13 : 11,
          opacity: 1,
          line: {
            ...(trace?.marker?.line ?? {}),
            color: palette.theme === "dark" ? "rgba(248,250,252,0.96)" : "rgba(255,255,255,0.98)",
            width: 1.8,
          },
        },
        textfont: { ...(trace?.textfont ?? {}), color: palette.text, size: 11 },
      };
    }

    return trace;
  }),
  layout: (() => {
    const layout = styleCommonLayout(bundle.layout, palette, { height: 420 });
    return {
      ...layout,
      height: 420,
      margin: { ...layout.margin, l: 58, r: 76, t: 8, b: 92 },
      showlegend: true,
      legend: {
        ...(layout.legend ?? {}),
        orientation: "h",
        x: 0.5,
        y: -0.18,
        xanchor: "center",
        yanchor: "top",
        bgcolor: "rgba(0,0,0,0)",
        borderwidth: 0,
        font: { ...((layout.legend ?? {}).font ?? {}), color: palette.legendText, size: 11 },
        itemclick: false,
        itemdoubleclick: false,
      },
      xaxis: mergeAxisLayout(layout?.xaxis, palette, { domain: [0, 1] }),
      yaxis: mergeAxisLayout(layout?.yaxis, palette, { domain: [0, 1], range: [0, 100], dtick: 20 }),
    };
  })(),
});

const styleHeatmapBundle = (
  bundle: PlotlyBundle,
  palette: ChartPalette,
  kind: HeatmapKind = "stress"
): PlotlyBundle => ({
  data: (bundle.data ?? []).map((trace: any) => ({
    ...trace,
    xgap: trace?.xgap ?? 2,
    ygap: trace?.ygap ?? 2,
    // Centralized, theme-aware fill overrides whatever named scale the backend
    // baked in. "diverging" = colorblind-safe orange(down) <-> blue(up) anchored
    // at 0 (matches the page's up=blue/down=orange language); "stress" = calm ->
    // amber -> red sequential for count/intensity grids.
    colorscale: heatmapColorscale(kind, palette.theme),
    ...(kind === "diverging" ? { zmid: 0 } : { zmin: 0 }),
    colorbar: {
      ...(trace?.colorbar ?? {}),
      outlinecolor: palette.border,
      outlinewidth: 1,
      thickness: 14,
      tickfont: { ...(trace?.colorbar?.tickfont ?? {}), color: palette.axisMuted, size: 11 },
      title: trace?.colorbar?.title
        ? typeof trace.colorbar.title === "string"
          ? { text: trace.colorbar.title, font: { color: palette.axis, size: 12 } }
          : { ...trace.colorbar.title, font: { ...(trace.colorbar.title.font ?? {}), color: palette.axis, size: 12 } }
        : trace?.colorbar?.title,
    },
  })),
  layout: (() => {
    const layout = styleCommonLayout(bundle.layout, palette, { height: 430 });
    return {
      ...layout,
      margin: { ...layout.margin, l: 72, r: 36, t: 24, b: 62 },
      xaxis: mergeAxisLayout(layout?.xaxis, palette, { tickangle: -30 }),
      yaxis: mergeAxisLayout(layout?.yaxis, palette),
    };
  })(),
});

type SpxRealizedMove = {
  error?: string;
  asof_date: string | null;
  close?: number;
  log_return?: number;
  return_pct?: number;
  z_robust?: number;
  z_standard?: number;
  abs_z_robust?: number;
  percentile_all?: number;
  percentile_up_tail?: number | null;
  percentile_down_tail?: number | null;
  band_label?: string;
  shock_cluster_21d?: number;
  shock_cluster_21d_percentile?: number;
  realized_vol_21d?: number;
  realized_vol_21d_percentile?: number;
  tape_days?: number;
  line_days?: number;
  plots?: {
    shock_tape?: PlotlyBundle;
    absz_percentile?: PlotlyBundle;
    recent_heatmap?: PlotlyBundle;
  };
  history_meta?: {
    anchor_start?: string;
    points_close?: number;
    points_return?: number;
    sigma_robust?: number;
    sigma_standard?: number;
    source?: string;
    stooq_url?: string | null;
    ibkr_history_enabled?: boolean;
  };
};

type LegendMeta = {
  meta_version?: string;
  page?: { title?: string; purpose?: string };
  sources?: Record<string, string>;
  tiles?: Record<
    string,
    {
      label?: string;
      units?: string;
      definition?: string;
      interpretation?: string;
    }
  >;
  spx_realized_move?: {
    section_title?: string;
    what_it_is?: string;
    metrics?: Record<
      string,
      {
        label?: string;
        units?: string;
        definition?: string;
        interpretation?: string;
        how_to_read?: string[];
        thresholds?: Record<string, { value?: number; label?: string }>;
      }
    >;
    charts?: Record<string, { title?: string; definition?: string }>;
  };
};

interface SnapshotData {
  current_vix_price: number | null;
  current_spx_price: number | null;
  vix_mean: number | null;
  vix_median: number | null;
  vix_zscore: number | null;
  vix_percentile: number | null;
  vix_2sd_threshold: number | null;
  vix_3sd_threshold: number | null;
  vix_asof_date?: string | null;
  // 2026-09-25: where the latest VIX close came from, and recent sessions with no close.
  vix_asof_source?: "ibkr_provisional" | "official" | "unknown" | null;
  vix_missing_sessions?: string[];
  vix_unconfirmed_sessions?: string[];
  date_generated: string;
  heatmap_2sd?: PlotlyBundle;
  heatmap_3sd?: PlotlyBundle;
  spx_realized_move?: SpxRealizedMove;
  meta?: LegendMeta;
}

/** ------------------------ formatting helpers ------------------------ */
// Type predicate (2026-09-02): the plain boolean form could not narrow its argument,
// which is what made every guarded use below light up when strict mode arrived.
const isFiniteNumber = (n?: number | null): n is number => typeof n === "number" && Number.isFinite(n);

const fmtNumber = (n?: number | null, digits = 2) => (isFiniteNumber(n) ? n.toFixed(digits) : "N/A");
const fmtInteger = (n?: number | null) => (isFiniteNumber(n) ? Math.round(n).toLocaleString() : "N/A");
const fmtPct = (n?: number | null, digits = 2) => (isFiniteNumber(n) ? `${n.toFixed(digits)}%` : "N/A");
const fmtMaybePct = (n?: number | null, digits = 1) => (isFiniteNumber(n) ? `${n.toFixed(digits)}%` : "N/A");
const fmtPercentRank = (n?: number | null, digits = 1) => {
  if (!isFiniteNumber(n)) return "N/A";
  const normalized = n >= 0 && n <= 1 ? n * 100 : n;
  const fixed = normalized.toFixed(digits);
  return `${fixed.replace(/\.0$/, "")}%`;
};
const fmtSignedNumber = (n?: number | null, digits = 2) => {
  if (!isFiniteNumber(n)) return "N/A";
  const value = n.toFixed(digits);
  return n > 0 ? `+${value}` : value;
};

const pctToDisplay = (n?: number | null, digits = 1) => {
  if (!isFiniteNumber(n)) return "N/A";
  return `${(n * 100).toFixed(digits)}%`;
};

const safeDateLabel = (value?: string | null) => {
  if (!value) return "N/A";
  try {
    return fmtLocalDateTime(value);
  } catch {
    return value;
  }
};

type RegimeInfo = {
  name: string;
  tone: Tone;
  description: string;
  posture: string;
  leapsTarget: string;
  defenseMode: string;
  cioAction: string;
};

const getRegimeInfo = (z?: number | null): RegimeInfo => {
  if (!isFiniteNumber(z)) {
    return {
      name: "Unclassified",
      tone: "neutral",
      description: "VIX z-score unavailable.",
      posture: "Observe",
      leapsTarget: "N/A",
      defenseMode: "Unknown",
      cioAction: "Validate market data",
    };
  }

  if (z >= 2.5) {
    return {
      name: "Panic",
      tone: "danger",
      description: "Volatility is in a tail regime; risk policy should prioritize defense and liquidity.",
      posture: "Defensive",
      leapsTarget: "5–10% policy band",
      defenseMode: "Active",
      cioAction: "Review risk before approvals",
    };
  }

  if (z >= 1.0) {
    return {
      name: "High-Vol",
      tone: "warning",
      description: "Volatility is elevated; convexity budget should be reduced versus normal state.",
      posture: "Reduced risk",
      leapsTarget: "15–20% policy band",
      defenseMode: z >= 1.5 ? "Active" : "Watch",
      cioAction: z >= 1.5 ? "Review risk before approvals" : "Monitor confirmations",
    };
  }

  if (z >= -1.25) {
    return {
      name: "Sweet-Spot",
      tone: "success",
      description: "Volatility is within the core operating range for the convexity sleeve.",
      posture: "Normal",
      leapsTarget: "40% funded cap",
      defenseMode: "Off",
      cioAction: "No market-state breach",
    };
  }

  if (z >= -1.5) {
    return {
      name: "Lean-In",
      tone: "info",
      description: "Volatility is below normal; policy can lean into convexity subject to hard caps.",
      posture: "Constructive",
      leapsTarget: "40% cap-limited",
      defenseMode: "Off",
      cioAction: "Review deployment capacity",
    };
  }

  return {
    name: "Very-Calm",
    tone: "warning",
    description: "Volatility is materially compressed; watch complacency while preserving cap discipline.",
    posture: "Cap-limited lean-in",
    leapsTarget: "40% cap-limited",
    defenseMode: "Off",
    cioAction: "Confirm vol surface quality",
  };
};

type DataHealth = {
  label: string;
  tone: Tone;
  detail: string;
};

const getDataHealth = (snapshot: SnapshotData | null, error: string | null): DataHealth => {
  if (!snapshot) {
    return {
      label: "No Data",
      tone: "danger",
      detail: "No market snapshot is currently available.",
    };
  }

  if (error) {
    return {
      label: "Source Degraded",
      tone: "warning",
      detail: "Showing the last good snapshot because the latest refresh failed.",
    };
  }

  // Prefer the TRUE VIX observation date. VIX is a daily series, so freshness must be
  // judged on the data's age in days — not minutes since the snapshot was generated.
  // date_generated is always "just now" on every fetch, so it can never flag a days-old
  // VIX from a weekend/holiday/FRED publish lag (2026-07-01 audit P1 #9/#27). The 4-day
  // threshold tolerates a normal weekend (Fri→Mon) without false-alarming; a genuine
  // multi-day lag trips it.
  const vixAsOf = snapshot.vix_asof_date ? Date.parse(snapshot.vix_asof_date) : NaN;
  if (Number.isFinite(vixAsOf)) {
    // Since 2026-09-25 the latest close may be IBKR's same-day capture, replaced by the
    // official Cboe/FRED close when that publishes. Say which one is on screen.
    const sourceNote =
      snapshot.vix_asof_source === "ibkr_provisional"
        ? " (IBKR close, awaiting official Cboe confirmation)"
        : snapshot.vix_asof_source === "unknown"
          ? " (source record unreadable)"
          : "";
    const ageDays = Math.max(0, (Date.now() - vixAsOf) / 86400000);
    if (ageDays > 4) {
      return {
        label: "Stale",
        tone: "warning",
        detail: `VIX data is ${Math.round(ageDays)} days old (as of ${snapshot.vix_asof_date}${sourceNote}).`,
      };
    }
    // A newer close can sit on top of missing sessions (IBKR captured today while Cboe
    // has not published earlier days). A current as-of date must not hide that gap.
    const missing = Array.isArray(snapshot.vix_missing_sessions) ? snapshot.vix_missing_sessions : [];
    if (missing.length > 0) {
      return {
        label: "Gaps",
        tone: "warning",
        detail: `VIX as of ${snapshot.vix_asof_date}${sourceNote}, but no close yet for ${missing.join(", ")}.`,
      };
    }
    // Normally at most one IBKR close is unconfirmed (tonight's, confirmed next morning).
    // Two or more means Cboe/FRED has fallen behind and those closes are IBKR's alone.
    const unconfirmed = Array.isArray(snapshot.vix_unconfirmed_sessions) ? snapshot.vix_unconfirmed_sessions : [];
    if (unconfirmed.length >= 2) {
      return {
        label: "Unconfirmed",
        tone: "warning",
        detail: `VIX as of ${snapshot.vix_asof_date}; ${unconfirmed.length} IBKR closes (${unconfirmed.join(", ")}) still await official Cboe confirmation.`,
      };
    }
    // "SPX spot is live." used to be asserted unconditionally. Pre-open the server-side
    // market-hours gate returns no quote, so current_spx_price is NULL and the tile
    // renders an em-dash -- while this sentence claimed the value beside it was live.
    // The number was never wrong; the sentence about it was.
    //
    // The two failure modes are deliberately distinguished: no quote because the market
    // is SHUT is expected and says so, whereas no quote DURING a session is a real
    // problem and must not be dressed up as a closed market.
    const spxSpotLive =
      typeof snapshot.current_spx_price === "number" && Number.isFinite(snapshot.current_spx_price);
    const spxNote = spxSpotLive
      ? "SPX spot is live."
      : isMarketOpenAt(Date.now())
        ? "SPX spot is unavailable."
        : "SPX spot unavailable — market closed.";
    return {
      label: "Fresh",
      tone: "success",
      detail: `VIX as of ${snapshot.vix_asof_date}${sourceNote}; ${spxNote}`,
    };
  }

  // Fallback only when no VIX data date is available: age the generation timestamp.
  const generatedAt = Date.parse(snapshot.date_generated);
  if (!Number.isFinite(generatedAt)) {
    return {
      label: "Timestamp Unknown",
      tone: "warning",
      detail: "Snapshot timestamp could not be parsed.",
    };
  }

  const ageMinutes = Math.max(0, (Date.now() - generatedAt) / 60000);
  if (ageMinutes > 240) {
    return {
      label: "Stale",
      tone: "warning",
      detail: `Snapshot is ${Math.round(ageMinutes / 60)} hours old.`,
    };
  }

  return {
    label: "Fresh",
    tone: "success",
    detail: `Snapshot generated ${Math.max(1, Math.round(ageMinutes))} minutes ago (no VIX data date returned).`,
  };
};

const fallbackMeta: LegendMeta = {
  meta_version: "fallback",
  page: {
    title: "Market Snapshot",
    purpose: "VIX context plus SPX realized stress telemetry for regime awareness.",
  },
  sources: {
    VIX: "FRED",
    SPX_spot: "IBKR",
    SPX_history: "Stooq",
  },
  tiles: {
    current_vix_price: {
      label: "VIX (FRED)",
      units: "index points",
      definition: "Most recent VIX level from FRED.",
      interpretation: "Higher VIX generally implies higher expected 30-day volatility—contextual, not a trade by itself.",
    },
    vix_zscore: {
      label: "VIX Z-Score",
      units: "z",
      definition: "Standardized VIX level vs its historical distribution.",
      interpretation: "Positive = above typical; negative = below typical.",
    },
    vix_percentile: {
      label: "VIX Percentile",
      units: "percentile (0–100)",
      definition: "Where current VIX sits vs history (0=low end, 100=high end).",
      interpretation: "A simple rank that works across eras.",
    },
    current_spx_price: {
      label: "SPX (IBKR)",
      units: "index points",
      definition: "Current SPX spot fetched from IBKR snapshot.",
      interpretation: "Reference price for context; not a return metric.",
    },
  },
  spx_realized_move: {
    section_title: "SPX Realized Move (1990–Present)",
    what_it_is: "Realized stress computed from SPX daily log returns since 1990 using robust z-scores (MAD-based).",
    metrics: {
      absz_percentile: {
        label: "|z| percentile (0–100)",
        units: "percentile (0–100)",
        definition: "Percentile rank of today’s absolute robust z-score vs all days since 1990.",
        how_to_read: [
          "Daily line is noisy; 5D MA is the regime signal.",
          "≥80 = elevated stress; ≥95 = extreme shock days.",
          "Persistent elevation = clustering risk.",
        ],
        thresholds: {
          elevated: { value: 80, label: "Elevated (80th)" },
          extreme: { value: 95, label: "Extreme (95th)" },
        },
      },
    },
    charts: {
      absz_percentile: { title: "|z| percentile vs 5D MA", definition: "Shock intensity rank (0–100) with 80/95 regime bands." },
      shock_tape: { title: "Shock Tape", definition: "Recent daily returns with marker size tied to |z|." },
      recent_heatmap: { title: "Recent Heatmap", definition: "Week-by-week robust z grid for clustering patterns." },
    },
  },
};

/** ------------------------ Tooltip (no external deps) ------------------------ */
const InfoTip: React.FC<{ title?: string; body?: React.ReactNode }> = ({ title, body }) => {
  const [open, setOpen] = useState(false);

  if (!title && !body) return null;

  return (
    <span className="relative inline-flex items-center">
      <button
        type="button"
        className="ml-2 inline-flex h-5 w-5 items-center justify-center rounded-full focus:outline-none focus:ring-2"
        style={{
          backgroundColor: ui.sectionBg,
          color: ui.textSecondary,
          border: `1px solid ${ui.cardBorder}`,
        }}
        aria-label="Info"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        <span className="text-xs font-bold">i</span>
      </button>

      {open && (
        <div
          className="absolute z-50 top-7 left-0 w-[320px] max-w-[85vw] rounded-lg p-3 text-sm"
          style={themeCardStyle}
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
        >
          {title && <div className="font-semibold mb-1" style={{ color: ui.textPrimary }}>{title}</div>}
          {body && <div className="leading-snug" style={{ color: ui.textSecondary }}>{body}</div>}
        </div>
      )}
    </span>
  );
};

function tipBody(definition?: string, interpretation?: string, units?: string, extra?: string[]) {
  const parts: React.ReactNode[] = [];
  if (definition) parts.push(<div key="d" className="mb-2">{definition}</div>);
  if (units) parts.push(<div key="u" className="mb-2"><span className="font-semibold">Units:</span> {units}</div>);
  if (interpretation) parts.push(<div key="i" className="mb-2"><span className="font-semibold">How to use:</span> {interpretation}</div>);
  if (extra && extra.length) {
    parts.push(
      <ul key="x" className="list-disc pl-5 space-y-1">
        {extra.map((t, idx) => (
          <li key={idx}>{t}</li>
        ))}
      </ul>
    );
  }
  return <div>{parts}</div>;
}

const ActionButton: React.FC<{
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}> = ({ children, onClick, disabled }) => (
  <button
    className="px-3 py-2 rounded-lg whitespace-nowrap text-xs font-semibold uppercase tracking-wider transition-colors disabled:opacity-60"
    style={buttonStyle}
    disabled={disabled}
    onMouseEnter={(e) => {
      if (!disabled) e.currentTarget.style.backgroundColor = ui.buttonBgHover;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.backgroundColor = ui.buttonBg;
    }}
    onClick={onClick}
  >
    {children}
  </button>
);

const StatusChip: React.FC<{ label: string; tone?: Tone; title?: string }> = ({ label, tone = "neutral", title }) => (
  <span
    className="inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider"
    style={chipStyle(tone as Tone)}
    title={title}
  >
    {label}
  </span>
);

const MetricCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  tone?: Tone;
  chip?: string;
  tip?: React.ReactNode;
}> = ({ label, value, sub, tone = "neutral", chip, tip }) => (
  <div className="rounded-2xl p-4 min-h-[126px] flex flex-col justify-between" style={themeCardStyle}>
    <div className="flex items-start justify-between gap-3">
      <div className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={mutedText}>
        {label}
        {tip}
      </div>
      {chip && <StatusChip label={chip} tone={tone} />}
    </div>
    <div className="mt-4 text-2xl md:text-3xl font-semibold tabular-nums tracking-tight" style={{ color: ui.textPrimary }}>
      {value}
    </div>
    {sub && <div className="mt-2 text-xs leading-snug" style={secondaryText}>{sub}</div>}
  </div>
);

const PanelTitle: React.FC<{ eyebrow?: string; title: string; children?: React.ReactNode }> = ({ eyebrow, title, children }) => (
  <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
    <div>
      {eyebrow && <div className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={mutedText}>{eyebrow}</div>}
      <h3 className="mt-1 text-lg font-semibold tracking-tight" style={{ color: ui.textPrimary }}>{title}</h3>
    </div>
    {children}
  </div>
);

/** True only when both values are finite and current has actually exceeded the level. */
const isBreached = (current?: number | null, threshold?: number | null) =>
  isFiniteNumber(current) && isFiniteNumber(threshold) && (current as number) > (threshold as number);

/**
 * Numeric context for the VIX event maps. The "2σ"/"3σ" labels are absolute VIX
 * levels (full-history mean + kσ, computed in HelionBackend strategy/vix_logic.py
 * -> compute_vix_thresholds), so the grids are unreadable without stating what
 * level the sigma actually is and where current VIX sits against it.
 */
const VixThresholdNote: React.FC<{
  sigmas: 2 | 3;
  threshold?: number | null;
  mean?: number | null;
  sigma?: number | null;
  currentVix?: number | null;
  asOf?: string | null;
}> = ({ sigmas, threshold, mean, sigma, currentVix, asOf }) => {
  if (!isFiniteNumber(threshold)) return null;
  const gap = isFiniteNumber(currentVix) ? (currentVix as number) - (threshold as number) : null;
  const strong = (text: string) => (
    <span className="font-semibold tabular-nums" style={{ color: ui.textPrimary }}>{text}</span>
  );

  return (
    <p className="-mt-2 mb-4 text-xs leading-relaxed" style={secondaryText}>
      Cells count the years in which VIX closed above {strong(fmtNumber(threshold))} on that calendar day
      {isFiniteNumber(mean) && isFiniteNumber(sigma) ? (
        <> — full-history mean {fmtNumber(mean)} + {sigmas} × σ {fmtNumber(sigma)}</>
      ) : null}
      .
      {gap != null && (
        <>
          {" "}Current VIX {fmtNumber(currentVix)}
          {asOf ? ` (as of ${asOf})` : ""} is {strong(fmtNumber(Math.abs(gap)))} points{" "}
          {gap >= 0 ? "above" : "below"} this line.
        </>
      )}
    </p>
  );
};

const toneValueColor = (tone: Tone) => (tone === "neutral" ? ui.textPrimary : String(toneStyles[tone].color ?? ui.textPrimary));

const ValueRow: React.FC<{ label: string; value: React.ReactNode; tone?: Tone }> = ({ label, value, tone }) => {
  const resolvedTone: Tone = tone ?? "neutral";
  return (
    <div className="flex items-center justify-between gap-4 border-b py-2.5 last:border-b-0" style={{ borderColor: ui.cardBorder }}>
      <span className="text-sm" style={secondaryText}>{label}</span>
      <span className="text-sm font-semibold tabular-nums text-right" style={{ color: toneValueColor(resolvedTone) }}>{value}</span>
    </div>
  );
};

const plotConfig = {
  responsive: true,
  displaylogo: false,
  modeBarButtonsToRemove: ["lasso2d", "select2d", "autoScale2d", "toggleSpikelines"],
} as any;

const primarySignalPlotConfig = {
  ...plotConfig,
  displayModeBar: false,
} as any;


const normalizePercentileValue = (value?: number | null): number | null => {
  if (!isFiniteNumber(value)) return null;
  if (value >= 0 && value <= 1) return value * 100;
  return value;
};

const lastFiniteArrayValue = (value: any): number | null => {
  if (Array.isArray(value)) {
    for (let i = value.length - 1; i >= 0; i -= 1) {
      const candidate = lastFiniteArrayValue(value[i]);
      if (candidate != null) return candidate;
    }
    return null;
  }

  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const traceMatches = (name: string, includeAll: string[], excludeAny: string[] = []) => {
  const normalizedName = name.toLowerCase();
  return includeAll.every((part) => normalizedName.includes(part.toLowerCase())) &&
    !excludeAny.some((part) => normalizedName.includes(part.toLowerCase()));
};

const extractLatestTraceValue = (bundle: PlotlyBundle | null | undefined, includeAll: string[], excludeAny: string[] = []) => {
  const traces = bundle?.data ?? [];
  for (const trace of traces) {
    if (!traceMatches(String(trace?.name ?? ""), includeAll, excludeAny)) continue;
    const yValue = lastFiniteArrayValue(trace?.y);
    if (yValue != null) return normalizePercentileValue(yValue);
  }
  return null;
};

const fmtOrdinal = (n?: number | null) => {
  if (!isFiniteNumber(n)) return "N/A";
  const value = Math.round(n);
  const mod100 = value % 100;
  const suffix = mod100 >= 11 && mod100 <= 13 ? "th" : value % 10 === 1 ? "st" : value % 10 === 2 ? "nd" : value % 10 === 3 ? "rd" : "th";
  return `${value}${suffix}`;
};

const percentileTone = (value?: number | null): Tone => {
  if (!isFiniteNumber(value)) return "neutral";
  if (value >= 95) return "danger"; // extreme — red
  if (value >= 80) return "orange"; // high — orange
  if (value >= 60) return "warning"; // elevated — amber
  return "info"; // calm — cool
};

// Color a market MOVE by its stress magnitude (|robust z|), never by direction.
// Direction is carried by the signed value itself (e.g. "-0.55%").
const moveStressTone = (zRobust?: number | null): Tone => {
  if (!isFiniteNumber(zRobust)) return "neutral";
  const z = Math.abs(zRobust);
  if (z >= 3) return "danger"; // extreme
  if (z >= 2) return "orange"; // high
  if (z >= 1) return "warning"; // elevated
  return "neutral"; // calm
};

const getInstitutionalNote = (abszPct: number | null, maPct: number | null, regimeName: string) => {
  if (!isFiniteNumber(abszPct)) {
    return `Awaiting live realized-stress telemetry. Maintain ${regimeName} policy posture until the signal refreshes.`;
  }

  const persistenceText = isFiniteNumber(maPct)
    ? abszPct >= maPct
      ? "above the 5D average"
      : "below the 5D average"
    : "relative to the current moving-average tape";

  if (abszPct >= 95) {
    return `Realized stress is in the extreme tail and ${persistenceText}. Keep CIO review tight and prioritize liquidity before approving new risk.`;
  }

  if (abszPct >= 80) {
    return `Stress is elevated ${persistenceText}. Maintain ${regimeName} risk posture and monitor persistence above the 80th percentile.`;
  }

  if (abszPct >= 60) {
    return `Stress is moderately elevated ${persistenceText} but still consistent with the ${regimeName} posture. Monitor for clustering before changing risk.`;
  }

  return `Realized stress is contained ${persistenceText}. Maintain ${regimeName} posture unless VIX, SPX trend, or term-structure confirmations deteriorate.`;
};

const SignalMetricRow: React.FC<{
  marker: string;
  label: string;
  value: React.ReactNode;
  tone?: Tone;
}> = ({ marker, label, value, tone }) => {
  const resolvedTone: Tone = tone ?? "neutral";
  return (
    <div className="flex items-center justify-between gap-4 border-b py-3 last:border-b-0" style={{ borderColor: ui.cardBorder }}>
      <div className="flex min-w-0 items-center gap-3">
        <span
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold uppercase tracking-wider"
          style={chipStyle(resolvedTone)}
        >
          {marker}
        </span>
        <span className="truncate text-sm md:text-base" style={{ color: ui.textPrimary }}>{label}</span>
      </div>
      <span className="shrink-0 text-sm md:text-base font-semibold tabular-nums" style={{ color: toneValueColor(resolvedTone) }}>{value}</span>
    </div>
  );
};

const MarketSnapshot: React.FC = () => {
  const { userLoading } = useUser();
  const { theme } = useTheme();

  const [data, setData] = useState<SnapshotData | null>(null);
  const lastGoodDataRef = useRef<SnapshotData | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showMethodology, setShowMethodology] = useState(false);
  const [showDiagnostics, setShowDiagnostics] = useState(false);

  const abortRef = useRef<AbortController | null>(null);

  const applyData = useCallback((incoming: SnapshotData) => {
    setData(incoming);
    lastGoodDataRef.current = incoming;
  }, []);

  const fetchSnapshot = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const url = `${API_BASE_URL}/market_snapshot?t=${Date.now()}`;
      const res = await fetch(url, { signal: controller.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status} – ${res.statusText}`);

      const incoming = (await res.json()) as SnapshotData;
      applyData(incoming);
    } catch (err: any) {
      if (err?.name === "AbortError") return;
      setError(err?.message || "Unknown error");
      if (lastGoodDataRef.current) setData(lastGoodDataRef.current);
    } finally {
      if (abortRef.current === controller) setLoading(false);
    }
  }, [applyData]);

  useEffect(() => {
    if (!userLoading) fetchSnapshot();
  }, [userLoading, fetchSnapshot]);

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  // Reuse the existing live-tape stream so the SPX Spot tile updates live (5s) with no
  // new backend producer. ONLY current_spx_price is overlaid from the push; the heavy
  // realized-move / VIX analysis stays the coherent on-load snapshot (it is close-based,
  // and the page itself notes intraday percentile is noise). Off-hours the broadcast is
  // idle, so the tile holds the last-loaded spot — no churn.
  // Holiday- and half-day-aware, from the same shared clock the Signals and Risk pages
  // use, so the three cannot disagree about whether the market is open.
  const spxMarketOpen = isMarketOpenAt(Date.now());

  const { connected: spxStreaming } = useSignalR(
    "liveTape",
    (payload: any) => {
      const live = payload?.spx?.live;
      if (typeof live !== "number" || !Number.isFinite(live)) return;
      const base = lastGoodDataRef.current ?? data;
      if (!base) return; // wait for the initial snapshot load before overlaying live spot
      const next = { ...base, current_spx_price: live };
      lastGoodDataRef.current = next;
      setData(next);
    },
    { enabled: !userLoading }
  );

  const effectiveData: SnapshotData | null = data ?? lastGoodDataRef.current ?? null;
  const meta: LegendMeta = effectiveData?.meta ?? fallbackMeta;
  const chartPalette = useMemo(() => getChartPalette(theme), [theme]);

  const styledPlots = useMemo(() => ({
    shockTape: effectiveData?.spx_realized_move?.plots?.shock_tape
      ? styleShockTapeBundle(effectiveData.spx_realized_move.plots.shock_tape, chartPalette)
      : null,
    abszPercentile: effectiveData?.spx_realized_move?.plots?.absz_percentile
      ? styleAbszBundle(effectiveData.spx_realized_move.plots.absz_percentile, chartPalette)
      : null,
    recentHeatmap: effectiveData?.spx_realized_move?.plots?.recent_heatmap
      ? styleHeatmapBundle(effectiveData.spx_realized_move.plots.recent_heatmap, chartPalette, "diverging")
      : null,
    heatmap2sd: effectiveData?.heatmap_2sd ? styleHeatmapBundle(effectiveData.heatmap_2sd, chartPalette, "stress") : null,
    heatmap3sd: effectiveData?.heatmap_3sd ? styleHeatmapBundle(effectiveData.heatmap_3sd, chartPalette, "stress") : null,
  }), [effectiveData, chartPalette]);

  if (userLoading) {
    return (
      <Layout>
        <div className="px-3 py-5" style={{ backgroundColor: ui.pageBg, color: ui.textPrimary }}>
          <p>Loading...</p>
        </div>
      </Layout>
    );
  }

  const spxMove = effectiveData?.spx_realized_move;
  const spxMoveError = spxMove?.error;
  const regime = getRegimeInfo(effectiveData?.vix_zscore ?? null);
  const dataHealth = getDataHealth(effectiveData, error);
  const historySource = spxMove?.history_meta?.source ?? "N/A";

  // The 2σ/3σ event maps are keyed off absolute VIX levels (full-history mean + kσ).
  // σ is recovered as (3σ level − 2σ level) so the panels can state the derivation
  // without adding a backend field. Null if either threshold is missing.
  const vixSigma =
    isFiniteNumber(effectiveData?.vix_2sd_threshold) && isFiniteNumber(effectiveData?.vix_3sd_threshold)
      ? (effectiveData!.vix_3sd_threshold as number) - (effectiveData!.vix_2sd_threshold as number)
      : null;

  const tailLabel = (() => {
    if (!spxMove) return "N/A";
    if ((spxMove.return_pct ?? 0) > 0 && spxMove.percentile_up_tail != null) {
      return `Rally severity ${fmtMaybePct(spxMove.percentile_up_tail)}`;
    }
    if ((spxMove.return_pct ?? 0) < 0 && spxMove.percentile_down_tail != null) {
      return `Selloff severity ${fmtMaybePct(spxMove.percentile_down_tail)}`;
    }
    return "Neutral tape";
  })();

  const abszPlotBundle = effectiveData?.spx_realized_move?.plots?.absz_percentile ?? null;
  const abszPercentileLive = extractLatestTraceValue(abszPlotBundle, ["today"], ["ma"]) ??
    extractLatestTraceValue(abszPlotBundle, ["|z|", "live"], ["ma"]) ??
    extractLatestTraceValue(abszPlotBundle, ["daily"]);
  const abszMovingAverageLive = extractLatestTraceValue(abszPlotBundle, ["5d", "live"]) ??
    extractLatestTraceValue(abszPlotBundle, ["5d ma"]) ??
    extractLatestTraceValue(abszPlotBundle, ["moving average"]);
  const signalTone = percentileTone(abszPercentileLive);
  const directionalPercentileValue = normalizePercentileValue(spxMove?.percentile_all ?? null);
  const directionalPercentile = fmtMaybePct(directionalPercentileValue);
  const signalNote = getInstitutionalNote(abszPercentileLive, abszMovingAverageLive, regime.name);

  const tileTip = (key: string) => {
    const t = meta.tiles?.[key];
    if (!t) return null;
    return <InfoTip title={t.label ?? key} body={tipBody(t.definition, t.interpretation, t.units)} />;
  };

  const spxMetricTip = (metricKey: string, titleFallback: string) => {
    const m = meta.spx_realized_move?.metrics?.[metricKey];
    if (!m) return null;
    return <InfoTip title={m.label ?? titleFallback} body={tipBody(m.definition, m.interpretation, m.units, m.how_to_read)} />;
  };

  const chartTip = (chartKey: string, titleFallback: string) => {
    const c = meta.spx_realized_move?.charts?.[chartKey];
    return <InfoTip title={c?.title ?? titleFallback} body={tipBody(c?.definition, undefined, undefined)} />;
  };

  return (
    <Layout>
      <div style={{ backgroundColor: ui.pageBg, color: ui.textPrimary }} className="px-3 py-5">
        <div className="mb-5 rounded-2xl p-5 md:p-6" style={themeCardStyle}>
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.22em]" style={mutedText}>
                Helion Strategies · Market Regime Command Center
              </div>
              <h1 className="mt-2 text-3xl font-semibold tracking-tight" style={{ color: ui.textPrimary }}>
                Market Snapshot
              </h1>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <StatusChip label={`Data ${dataHealth.label}`} tone={dataHealth.tone} title={dataHealth.detail} />
                <StatusChip label={`Regime ${regime.name}`} tone={regime.tone} title={regime.description} />
                <StatusChip label="Read-only" tone="neutral" title="This view does not execute trades." />
              </div>
              <p className="mt-3 max-w-3xl text-sm leading-relaxed" style={secondaryText}>
                A CIO-facing regime view for VIX, SPX realized stress, and policy impact. Snapshot outputs are informational only; execution remains routed through signal generation, PendingOrders, CIO approval, and audit logging.
              </p>
            </div>

            <div className="flex flex-col items-start gap-3 sm:flex-row lg:items-center">
              <ActionButton onClick={() => setShowMethodology((v) => !v)}>
                {showMethodology ? "Hide" : "Methodology"}
              </ActionButton>
              <ActionButton onClick={fetchSnapshot} disabled={loading}>
                {loading ? "Refreshing" : "Refresh"}
              </ActionButton>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-4">
            <div className="rounded-xl p-4 md:col-span-2" style={sectionCardStyle}>
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={mutedText}>VIX data as of</div>
              <div className="mt-1 text-sm font-semibold" style={{ color: ui.textPrimary }}>
                {safeDateLabel(effectiveData?.vix_asof_date ?? effectiveData?.date_generated)}
              </div>
              <div className="mt-1 text-xs" style={secondaryText}>{dataHealth.detail}</div>
            </div>
            <div className="rounded-xl p-4" style={sectionCardStyle}>
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={mutedText}>Helion Posture</div>
              <div className="mt-1 text-sm font-semibold" style={{ color: ui.textPrimary }}>{regime.posture}</div>
              <div className="mt-1 text-xs" style={secondaryText}>Defense mode: {regime.defenseMode}</div>
            </div>
            <div className="rounded-xl p-4" style={sectionCardStyle}>
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={mutedText}>CIO Action</div>
              <div className="mt-1 text-sm font-semibold" style={{ color: ui.textPrimary }}>{regime.cioAction}</div>
              <div className="mt-1 text-xs" style={secondaryText}>No auto-execution from this dashboard.</div>
            </div>
          </div>
        </div>

        {showMethodology && (
          <div className="mb-6 rounded-2xl p-5" style={themeCardStyle}>
            <PanelTitle eyebrow="Reference" title="Methodology & Sources">
              <StatusChip label={meta.meta_version ? `Meta ${meta.meta_version}` : "Meta fallback"} tone="neutral" />
            </PanelTitle>

            {meta.page?.purpose && <p className="text-sm leading-relaxed" style={secondaryText}>{meta.page.purpose}</p>}

            <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-3">
              <div className="rounded-xl p-4" style={sectionCardStyle}>
                <div className="text-sm font-semibold" style={{ color: ui.textPrimary }}>Sources</div>
                <ul className="mt-3 list-disc space-y-1 pl-5 text-sm" style={secondaryText}>
                  {Object.entries(meta.sources ?? fallbackMeta.sources ?? {}).map(([k, v]) => (
                    <li key={k}>
                      <span className="font-semibold" style={{ color: ui.textPrimary }}>{k}:</span> {v}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-xl p-4" style={sectionCardStyle}>
                <div className="text-sm font-semibold" style={{ color: ui.textPrimary }}>VIX Regime Bands</div>
                <div className="mt-3 space-y-2 text-sm" style={secondaryText}>
                  <ValueRow label="Panic" value="z ≥ 2.50" tone="danger" />
                  <ValueRow label="High-Vol" value="1.00 to 2.49" tone="warning" />
                  <ValueRow label="Sweet-Spot" value="−1.25 to 0.99" tone="success" />
                  <ValueRow label="Lean-In / Very-Calm" value="z < −1.25" tone="info" />
                </div>
              </div>

              <div className="rounded-xl p-4" style={sectionCardStyle}>
                <div className="text-sm font-semibold" style={{ color: ui.textPrimary }}>SPX Realized Stress</div>
                <p className="mt-3 text-sm leading-relaxed" style={secondaryText}>
                  {meta.spx_realized_move?.what_it_is ?? fallbackMeta.spx_realized_move?.what_it_is}
                </p>
                <ul className="mt-3 list-disc space-y-1 pl-5 text-sm" style={secondaryText}>
                  {(meta.spx_realized_move?.metrics?.absz_percentile?.how_to_read ?? fallbackMeta.spx_realized_move?.metrics?.absz_percentile?.how_to_read ?? []).map((t, idx) => (
                    <li key={idx}>{t}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {error && effectiveData && (
          <div className="mb-5 rounded-xl p-4" style={{ ...themeCardStyle, borderColor: "rgba(217,119,6,0.40)" }}>
            <div className="text-sm font-semibold" style={{ color: "#d97706" }}>Live refresh degraded</div>
            <div className="mt-1 text-sm" style={secondaryText}>Showing the last good snapshot. Error: {error}</div>
          </div>
        )}

        {!effectiveData && !loading && error && (
          <div className="rounded-xl p-4" style={{ ...themeCardStyle, borderColor: "rgba(220,38,38,0.40)" }}>
            <div className="text-sm font-semibold" style={{ color: "#dc2626" }}>Market snapshot unavailable</div>
            <div className="mt-1 text-sm" style={secondaryText}>Error: {error}</div>
          </div>
        )}

        {loading && !effectiveData && (
          <p className="italic" style={secondaryText}>Loading market data…</p>
        )}

        {effectiveData && (
          <>
            <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-6">
              <MetricCard
                label="SPX Spot"
                value={fmtInteger(effectiveData.current_spx_price)}
                sub="IBKR snapshot reference level"
                /* `spxStreaming` is useSignalR's `connected` -- the SOCKET being
                   established, not data arriving. The broadcaster is gated off outside
                   trading hours, so pre-open the socket connects, nothing is pushed, the
                   value is null and the tile renders an em-dash -- and this chip still
                   read "Live" beside it. Market session decides the label; the socket
                   only distinguishes streaming from polling once the market is open. */
                chip={!spxMarketOpen ? "Closed" : spxStreaming ? "Live" : "Spot"}
                tone="neutral"
                tip={tileTip("current_spx_price")}
              />
              <MetricCard
                label="VIX"
                value={fmtNumber(effectiveData.current_vix_price)}
                sub={`Mean ${fmtNumber(effectiveData.vix_mean)} · Median ${fmtNumber(effectiveData.vix_median)}`}
                chip="Implied"
                tone="info"
                tip={tileTip("current_vix_price")}
              />
              <MetricCard
                label="VIX Z-Score"
                value={fmtSignedNumber(effectiveData.vix_zscore)}
                sub={regime.description}
                chip={regime.name}
                tone={regime.tone}
                tip={tileTip("vix_zscore")}
              />
              <MetricCard
                label="VIX Percentile"
                value={fmtPercentRank(effectiveData.vix_percentile)}
                sub={`Event-map levels · 2σ VIX ${fmtNumber(effectiveData.vix_2sd_threshold)} · 3σ VIX ${fmtNumber(effectiveData.vix_3sd_threshold)}`}
                chip="Rank"
                tone="neutral"
                tip={tileTip("vix_percentile")}
              />
              <MetricCard
                label="SPX Realized Move"
                value={fmtPct(spxMove?.return_pct ?? null)}
                sub={`Robust z ${fmtSignedNumber(spxMove?.z_robust ?? null)} · ${spxMove?.band_label ?? "Unclassified"}`}
                chip={spxMove?.band_label ?? "Realized"}
                tone={moveStressTone(spxMove?.z_robust)}
                tip={spxMetricTip("return_pct", "SPX realized move")}
              />
              <MetricCard
                label="21D Realized Vol"
                value={pctToDisplay(spxMove?.realized_vol_21d ?? null)}
                sub={`Percentile ${fmtMaybePct(spxMove?.realized_vol_21d_percentile ?? null)}`}
                chip="Realized"
                tone="neutral"
                tip={spxMetricTip("realized_vol_21d", "Realized Vol (21d)")}
              />
            </div>

            {spxMoveError && (
              <div className="mb-5 rounded-xl p-4" style={{ ...themeCardStyle, borderColor: "rgba(220,38,38,0.40)" }}>
                <div className="text-sm font-semibold" style={{ color: "#dc2626" }}>SPX realized-move module error</div>
                <div className="mt-1 text-sm" style={secondaryText}>{spxMoveError}</div>
              </div>
            )}

            <div className="mb-6 grid grid-cols-1 gap-6 xl:grid-cols-12">
              <div className="xl:col-span-12 rounded-2xl p-5 min-h-[520px]" style={themeCardStyle}>
                <PanelTitle eyebrow="Primary Signal" title="SPX Realized Stress · |z| Percentile">
                  <div className="flex items-center gap-2">
                    <StatusChip label={`As of ${spxMove?.asof_date ?? "N/A"}`} tone="neutral" />
                    {chartTip("absz_percentile", "|z| percentile")}
                  </div>
                </PanelTitle>

                {styledPlots.abszPercentile?.data && styledPlots.abszPercentile?.layout ? (
                  <div className="mt-2 w-full">
                    <Plot
                      data={styledPlots.abszPercentile.data}
                      layout={styledPlots.abszPercentile.layout}
                      config={primarySignalPlotConfig}
                      useResizeHandler
                      style={{ width: "100%", height: "420px" }}
                    />
                  </div>
                ) : (
                  <div className="flex h-[420px] items-center justify-center rounded-xl" style={sectionCardStyle}>
                    <span className="text-sm" style={secondaryText}>No |z| percentile chart available.</span>
                  </div>
                )}
              </div>

              <div className="xl:col-span-6 rounded-2xl p-5 min-h-[430px]" style={themeCardStyle}>
                <PanelTitle eyebrow="Policy Impact" title="Helion Risk Posture">
                  <StatusChip label={regime.name} tone={regime.tone} />
                </PanelTitle>
                <div className="space-y-1">
                  <ValueRow label="Current band" value={regime.name} tone={regime.tone} />
                  <ValueRow label="LEAPS policy target" value={regime.leapsTarget} tone={regime.tone} />
                  <ValueRow label="Funded LEAPS cap" value="40%" tone="neutral" />
                  <ValueRow label="Defense mode" value={regime.defenseMode} tone={regime.defenseMode === "Active" ? "warning" : "neutral"} />
                  <ValueRow label="CIO review" value={regime.cioAction} tone={regime.tone} />
                  <ValueRow label="Execution mode" value="PendingOrders only" tone="neutral" />
                </div>
                <p className="mt-5 text-xs leading-relaxed" style={mutedText}>
                  This panel maps market state to policy posture only. It does not create, approve, or execute orders.
                </p>
              </div>

              <div className="xl:col-span-6 rounded-2xl p-5 min-h-[252px]" style={themeCardStyle}>
                <PanelTitle eyebrow="Realized Stress" title="Current SPX Tape" />
                <div className="space-y-1">
                  <ValueRow label="Date" value={spxMove?.asof_date ?? "N/A"} />
                  <ValueRow label="Return" value={fmtPct(spxMove?.return_pct ?? null)} tone={moveStressTone(spxMove?.z_robust)} />
                  <ValueRow label="Robust z" value={fmtSignedNumber(spxMove?.z_robust ?? null)} tone={moveStressTone(spxMove?.z_robust)} />
                  <ValueRow label="Directional percentile" value={directionalPercentile} />
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-2 text-xs" style={mutedText}>
                  <StatusChip label={tailLabel} tone="neutral" />
                  <span>Source: {historySource}</span>
                </div>
              </div>

              <div className="xl:col-span-12 rounded-2xl p-5 min-h-[252px]" style={themeCardStyle}>
                <PanelTitle eyebrow="Signal Summary" title="Key Signal Metrics" />
                <div className="grid grid-cols-1 gap-5 lg:grid-cols-5">
                  <div className="lg:col-span-3">
                    <SignalMetricRow marker="Z" label="|z| Percentile (Live)" value={fmtMaybePct(abszPercentileLive)} tone={signalTone} />
                    <SignalMetricRow marker="MA" label="5D Moving Average (Live)" value={fmtMaybePct(abszMovingAverageLive)} tone={percentileTone(abszMovingAverageLive)} />
                    <SignalMetricRow marker="R" label="Percentile Rank (1Y)" value={fmtOrdinal(abszPercentileLive)} tone="neutral" />
                    <SignalMetricRow marker="P" label="Policy Posture" value={regime.name} tone={regime.tone} />
                  </div>
                  <div className="mt-2 border-t pt-4 lg:col-span-2 lg:mt-0 lg:border-l lg:border-t-0 lg:pl-5 lg:pt-0" style={{ borderColor: ui.cardBorder }}>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={mutedText}>Institutional Note</div>
                    <p className="mt-3 text-sm leading-relaxed" style={secondaryText}>{signalNote}</p>
                    <div className="mt-5 text-[11px] font-semibold uppercase tracking-[0.18em]" style={mutedText}>Updated</div>
                    <div className="mt-1 text-sm tabular-nums" style={{ color: ui.textPrimary }}>{spxMove?.asof_date ?? "N/A"}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-6 rounded-2xl p-5" style={themeCardStyle}>
              <PanelTitle eyebrow="Secondary Signal" title="Shock Tape">
                {chartTip("shock_tape", "Shock Tape")}
              </PanelTitle>
              {styledPlots.shockTape?.data && styledPlots.shockTape?.layout ? (
                <Plot
                  data={styledPlots.shockTape.data}
                  layout={styledPlots.shockTape.layout}
                  config={plotConfig}
                  style={{ width: "100%", height: "420px" }}
                />
              ) : (
                <div className="flex h-[260px] items-center justify-center rounded-xl" style={sectionCardStyle}>
                  <span className="text-sm" style={secondaryText}>No shock tape chart available.</span>
                </div>
              )}
            </div>

            <div className="mb-6 rounded-2xl p-5" style={themeCardStyle}>
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={mutedText}>Diagnostics</div>
                  <h3 className="mt-1 text-lg font-semibold tracking-tight" style={{ color: ui.textPrimary }}>
                    Event Maps & Historical Heatmaps
                  </h3>
                  <p className="mt-1 text-sm" style={secondaryText}>
                    Deeper diagnostics are collapsed by default to keep the primary regime view clean.
                  </p>
                </div>
                <ActionButton onClick={() => setShowDiagnostics((v) => !v)}>
                  {showDiagnostics ? "Hide Diagnostics" : "Show Diagnostics"}
                </ActionButton>
              </div>

              {showDiagnostics && (
                <div className="mt-6 space-y-6">
                  {styledPlots.recentHeatmap?.data && styledPlots.recentHeatmap?.layout && (
                    <div className="rounded-xl p-4" style={sectionCardStyle}>
                      <PanelTitle eyebrow="SPX" title="Recent Return Heatmap">
                        {chartTip("recent_heatmap", "Recent Heatmap")}
                      </PanelTitle>
                      <Plot
                        data={styledPlots.recentHeatmap.data}
                        layout={styledPlots.recentHeatmap.layout}
                        config={plotConfig}
                        style={{ width: "100%", height: "420px" }}
                      />
                    </div>
                  )}

                  {styledPlots.heatmap2sd?.data && styledPlots.heatmap2sd?.layout && (
                    <div className="rounded-xl p-4" style={sectionCardStyle}>
                      <PanelTitle eyebrow="VIX" title="VIX 2σ Event Map">
                        <div className="flex items-center gap-2">
                          <StatusChip
                            label={`VIX > ${fmtNumber(effectiveData.vix_2sd_threshold)}`}
                            tone={isBreached(effectiveData.current_vix_price, effectiveData.vix_2sd_threshold) ? "danger" : "neutral"}
                            title={
                              isBreached(effectiveData.current_vix_price, effectiveData.vix_2sd_threshold)
                                ? `Current VIX ${fmtNumber(effectiveData.current_vix_price)} is above the 2σ threshold.`
                                : `Current VIX ${fmtNumber(effectiveData.current_vix_price)} is below the 2σ threshold.`
                            }
                          />
                          <InfoTip
                            title="VIX 2σ Event Map"
                            body={tipBody(
                              `Counts, for each calendar day, how many years VIX closed above its 2σ threshold of ${fmtNumber(effectiveData.vix_2sd_threshold)}.`,
                              "Use this to identify volatility clusters and regime transitions.",
                              "Threshold in VIX index points · cells are a count of years",
                              [
                                `Threshold = full-history mean ${fmtNumber(effectiveData.vix_mean)} + 2 × σ ${fmtNumber(vixSigma)}.`,
                                `Current VIX ${fmtNumber(effectiveData.current_vix_price)} as of ${effectiveData.vix_asof_date ?? "N/A"}.`,
                                "Computed on the full VIX history, so the level shifts only slowly as new data arrives.",
                                "Different statistic from the VIX Z-Score tile, which uses a trailing 252-day baseline.",
                              ]
                            )}
                          />
                        </div>
                      </PanelTitle>
                      <VixThresholdNote
                        sigmas={2}
                        threshold={effectiveData.vix_2sd_threshold}
                        mean={effectiveData.vix_mean}
                        sigma={vixSigma}
                        currentVix={effectiveData.current_vix_price}
                        asOf={effectiveData.vix_asof_date}
                      />
                      <Plot
                        data={styledPlots.heatmap2sd.data}
                        layout={styledPlots.heatmap2sd.layout}
                        config={plotConfig}
                        style={{ width: "100%", height: "500px" }}
                      />
                    </div>
                  )}

                  {styledPlots.heatmap3sd?.data && styledPlots.heatmap3sd?.layout && (
                    <div className="rounded-xl p-4" style={sectionCardStyle}>
                      <PanelTitle eyebrow="VIX" title="VIX 3σ Event Map">
                        <div className="flex items-center gap-2">
                          <StatusChip
                            label={`VIX > ${fmtNumber(effectiveData.vix_3sd_threshold)}`}
                            tone={isBreached(effectiveData.current_vix_price, effectiveData.vix_3sd_threshold) ? "danger" : "neutral"}
                            title={
                              isBreached(effectiveData.current_vix_price, effectiveData.vix_3sd_threshold)
                                ? `Current VIX ${fmtNumber(effectiveData.current_vix_price)} is above the 3σ threshold.`
                                : `Current VIX ${fmtNumber(effectiveData.current_vix_price)} is below the 3σ threshold.`
                            }
                          />
                          <InfoTip
                            title="VIX 3σ Event Map"
                            body={tipBody(
                              `Counts, for each calendar day, how many years VIX closed above its 3σ threshold of ${fmtNumber(effectiveData.vix_3sd_threshold)}.`,
                              "Highlights rare, high-stress volatility events.",
                              "Threshold in VIX index points · cells are a count of years",
                              [
                                `Threshold = full-history mean ${fmtNumber(effectiveData.vix_mean)} + 3 × σ ${fmtNumber(vixSigma)}.`,
                                `Current VIX ${fmtNumber(effectiveData.current_vix_price)} as of ${effectiveData.vix_asof_date ?? "N/A"}.`,
                                "Computed on the full VIX history, so the level shifts only slowly as new data arrives.",
                                "Different statistic from the VIX Z-Score tile, which uses a trailing 252-day baseline.",
                              ]
                            )}
                          />
                        </div>
                      </PanelTitle>
                      <VixThresholdNote
                        sigmas={3}
                        threshold={effectiveData.vix_3sd_threshold}
                        mean={effectiveData.vix_mean}
                        sigma={vixSigma}
                        currentVix={effectiveData.current_vix_price}
                        asOf={effectiveData.vix_asof_date}
                      />
                      <Plot
                        data={styledPlots.heatmap3sd.data}
                        layout={styledPlots.heatmap3sd.layout}
                        config={plotConfig}
                        style={{ width: "100%", height: "500px" }}
                      />
                    </div>
                  )}

                  {!styledPlots.recentHeatmap && !styledPlots.heatmap2sd && !styledPlots.heatmap3sd && (
                    <div className="rounded-xl p-4 text-sm" style={sectionCardStyle}>
                      No diagnostics charts are available in this snapshot.
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {!effectiveData && !loading && !error && (
          <p style={secondaryText}>No snapshot data available yet.</p>
        )}
      </div>
    </Layout>
  );
};

export default MarketSnapshot;
